import React, { useState } from 'react';
import { AlertCircleIcon, BuildingIcon, CheckCircleIcon, GlobeIcon, KeyIcon, LockIcon, MailIcon, MapPinIcon, PhoneIcon, PlusCircleIcon, ShieldIcon, TrashIcon, UserIcon, DollarSignIcon, EyeIcon } from 'lucide-react';
export function VendorProfile({
  vendor
}) {
  const [profile, setProfile] = useState({
    name: vendor.name || '',
    contactName: vendor.contactName || '',
    email: vendor.email || '',
    phone: vendor.phone || '',
    country: vendor.country || '',
    currency: vendor.currency || '',
    taxId: vendor.taxId || '',
    paymentTerms: vendor.paymentTerms || '',
    // Initialize with a sample bank account or empty array
    bankAccounts: vendor.bankAccounts || [{
      id: '1',
      bankName: vendor.bankName || '',
      accountNumber: vendor.accountNumber || '',
      routingNumber: vendor.routingNumber || '',
      isPrimary: true,
      nickname: 'Primary Account'
    }],
    // Initialize addresses array
    addresses: vendor.addresses || [{
      id: '1',
      type: 'Billing',
      street: vendor.address || '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      isPrimary: true
    }],
    // Initialize contacts array
    contacts: vendor.contacts || [{
      id: '1',
      name: vendor.contactName || '',
      title: '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      isPrimary: true
    }]
  });
  // Bank update security states
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [editingBankId, setEditingBankId] = useState(null);
  const [isAddingBank, setIsAddingBank] = useState(false);
  const [showBankVerification, setShowBankVerification] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [bankUpdateSuccess, setBankUpdateSuccess] = useState(false);
  const [securityTimer, setSecurityTimer] = useState(0);
  // Temp bank details (only applied after verification)
  const [tempBankDetails, setTempBankDetails] = useState({
    id: '',
    bankName: '',
    accountNumber: '',
    routingNumber: '',
    isPrimary: false,
    nickname: ''
  });
  // Address states
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [tempAddressDetails, setTempAddressDetails] = useState({
    id: '',
    type: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    isPrimary: false
  });
  // Contact states
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [tempContactDetails, setTempContactDetails] = useState({
    id: '',
    name: '',
    title: '',
    email: '',
    phone: '',
    isPrimary: false
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  // Add new state for password verification
  const [passwordVerification, setPasswordVerification] = useState({
    showVerification: false,
    password: '',
    isVerifying: false,
    error: '',
    bankAccountToView: null
  });
  // Add state to track which bank accounts are visible
  const [visibleBankAccounts, setVisibleBankAccounts] = useState({});
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setProfile({
      ...profile,
      [name]: value
    });
  };
  const handleBankDetailsChange = e => {
    const {
      name,
      value
    } = e.target;
    setTempBankDetails({
      ...tempBankDetails,
      [name]: value
    });
  };
  const handleAddressDetailsChange = e => {
    const {
      name,
      value
    } = e.target;
    setTempAddressDetails({
      ...tempAddressDetails,
      [name]: value
    });
  };
  const handleContactDetailsChange = e => {
    const {
      name,
      value
    } = e.target;
    setTempContactDetails({
      ...tempContactDetails,
      [name]: value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      setIsSaving(false);
      setIsEditing(false);
      setUpdateSuccess(true);
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 5000);
    }, 1000);
  };
  // Bank account management
  const startBankDetailsUpdate = bankAccount => {
    // Initialize temp bank details with current values
    setTempBankDetails({
      id: bankAccount.id,
      bankName: bankAccount.bankName,
      accountNumber: bankAccount.accountNumber,
      routingNumber: bankAccount.routingNumber,
      isPrimary: bankAccount.isPrimary,
      nickname: bankAccount.nickname || `Account ${bankAccount.id}`
    });
    setEditingBankId(bankAccount.id);
    setIsEditingBank(true);
    setIsAddingBank(false);
  };
  const startAddNewBank = () => {
    // Initialize temp bank details with empty values for a new account
    const newId = `new-bank-${Date.now()}`; // Temporary ID until saved
    setTempBankDetails({
      id: newId,
      bankName: '',
      accountNumber: '',
      routingNumber: '',
      isPrimary: profile.bankAccounts.length === 0,
      nickname: `Account ${profile.bankAccounts.length + 1}`
    });
    setEditingBankId(null);
    setIsEditingBank(true);
    setIsAddingBank(true);
  };
  const requestVerificationCodes = () => {
    // Reset verification status
    setEmailVerified(false);
    setSmsVerified(false);
    setVerificationError('');
    // Simulate sending verification codes
    setShowBankVerification(true);
    // Start security timer (2 minutes)
    setSecurityTimer(120);
    const interval = setInterval(() => {
      setSecurityTimer(prevTime => {
        if (prevTime <= 1) {
          clearInterval(interval);
          // Reset verification if timer expires
          if (!emailVerified || !smsVerified) {
            setShowBankVerification(false);
            setVerificationError('Verification timed out. Please try again.');
          }
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    // Simulate sending codes
    console.log('Email verification code sent to', profile.email);
    console.log('SMS verification code sent to', profile.phone);
  };
  const verifyEmailCode = () => {
    // In a real app, this would validate against a server-generated code
    // For demo purposes, we'll accept "123456" as the valid code
    if (emailCode === '123456') {
      setEmailVerified(true);
      setVerificationError('');
    } else {
      setVerificationError('Invalid email verification code');
    }
  };
  const verifySmsCode = () => {
    // In a real app, this would validate against a server-generated code
    // For demo purposes, we'll accept "654321" as the valid code
    if (smsCode === '654321') {
      setSmsVerified(true);
      setVerificationError('');
    } else {
      setVerificationError('Invalid SMS verification code');
    }
  };
  const cancelBankUpdate = () => {
    setIsEditingBank(false);
    setShowBankVerification(false);
    setEmailCode('');
    setSmsCode('');
    setEmailVerified(false);
    setSmsVerified(false);
    setVerificationError('');
    setEditingBankId(null);
    setIsAddingBank(false);
  };
  const submitBankUpdate = () => {
    // Only proceed if both email and SMS are verified
    if (!emailVerified || !smsVerified) {
      setVerificationError('Both email and SMS verification required');
      return;
    }
    // Update the profile with the temporary bank details
    if (isAddingBank) {
      // Add new bank account
      setProfile({
        ...profile,
        bankAccounts: [...profile.bankAccounts, {
          ...tempBankDetails,
          id: `bank-${Date.now()}` // Generate a more permanent ID
        }]
      });
    } else {
      // Update existing bank account
      setProfile({
        ...profile,
        bankAccounts: profile.bankAccounts.map(account => account.id === tempBankDetails.id ? tempBankDetails : account)
      });
    }
    // Reset states
    setIsEditingBank(false);
    setShowBankVerification(false);
    setEmailCode('');
    setSmsCode('');
    setEmailVerified(false);
    setSmsVerified(false);
    setEditingBankId(null);
    setIsAddingBank(false);
    // Show success message
    setBankUpdateSuccess(true);
    setTimeout(() => {
      setBankUpdateSuccess(false);
    }, 5000);
  };
  const setPrimaryAccount = id => {
    setProfile({
      ...profile,
      bankAccounts: profile.bankAccounts.map(account => ({
        ...account,
        isPrimary: account.id === id
      }))
    });
  };
  const removeAccount = id => {
    // Don't allow removing the primary account
    const accountToRemove = profile.bankAccounts.find(account => account.id === id);
    if (accountToRemove.isPrimary) {
      alert('You cannot remove the primary account. Please set another account as primary first.');
      return;
    }
    // Remove the account
    setProfile({
      ...profile,
      bankAccounts: profile.bankAccounts.filter(account => account.id !== id)
    });
  };
  // Address management
  const startAddressUpdate = address => {
    setTempAddressDetails({
      id: address.id,
      type: address.type,
      street: address.street,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      isPrimary: address.isPrimary
    });
    setEditingAddressId(address.id);
    setIsEditingAddress(true);
  };
  const startAddNewAddress = () => {
    const newId = `new-address-${Date.now()}`;
    setTempAddressDetails({
      id: newId,
      type: 'Billing',
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      isPrimary: profile.addresses.length === 0
    });
    setEditingAddressId(null);
    setIsEditingAddress(true);
  };
  const cancelAddressUpdate = () => {
    setIsEditingAddress(false);
    setEditingAddressId(null);
  };
  const submitAddressUpdate = () => {
    if (editingAddressId) {
      // Update existing address
      setProfile({
        ...profile,
        addresses: profile.addresses.map(address => address.id === editingAddressId ? tempAddressDetails : address)
      });
    } else {
      // Add new address
      setProfile({
        ...profile,
        addresses: [...profile.addresses, {
          ...tempAddressDetails,
          id: `address-${Date.now()}`
        }]
      });
    }
    setIsEditingAddress(false);
    setEditingAddressId(null);
    setUpdateSuccess(true);
    setTimeout(() => {
      setUpdateSuccess(false);
    }, 5000);
  };
  const setPrimaryAddress = id => {
    setProfile({
      ...profile,
      addresses: profile.addresses.map(address => ({
        ...address,
        isPrimary: address.id === id
      }))
    });
  };
  const removeAddress = id => {
    // Don't allow removing the primary address
    const addressToRemove = profile.addresses.find(address => address.id === id);
    if (addressToRemove.isPrimary) {
      alert('You cannot remove the primary address. Please set another address as primary first.');
      return;
    }
    // Remove the address
    setProfile({
      ...profile,
      addresses: profile.addresses.filter(address => address.id !== id)
    });
  };
  // Contact management
  const startContactUpdate = contact => {
    setTempContactDetails({
      id: contact.id,
      name: contact.name,
      title: contact.title,
      email: contact.email,
      phone: contact.phone,
      isPrimary: contact.isPrimary
    });
    setEditingContactId(contact.id);
    setIsEditingContact(true);
  };
  const startAddNewContact = () => {
    const newId = `new-contact-${Date.now()}`;
    setTempContactDetails({
      id: newId,
      name: '',
      title: '',
      email: '',
      phone: '',
      isPrimary: profile.contacts.length === 0
    });
    setEditingContactId(null);
    setIsEditingContact(true);
  };
  const cancelContactUpdate = () => {
    setIsEditingContact(false);
    setEditingContactId(null);
  };
  const submitContactUpdate = () => {
    if (editingContactId) {
      // Update existing contact
      setProfile({
        ...profile,
        contacts: profile.contacts.map(contact => contact.id === editingContactId ? tempContactDetails : contact)
      });
    } else {
      // Add new contact
      setProfile({
        ...profile,
        contacts: [...profile.contacts, {
          ...tempContactDetails,
          id: `contact-${Date.now()}`
        }]
      });
    }
    setIsEditingContact(false);
    setEditingContactId(null);
    setUpdateSuccess(true);
    setTimeout(() => {
      setUpdateSuccess(false);
    }, 5000);
  };
  const setPrimaryContact = id => {
    setProfile({
      ...profile,
      contacts: profile.contacts.map(contact => ({
        ...contact,
        isPrimary: contact.id === id
      }))
    });
  };
  const removeContact = id => {
    // Don't allow removing the primary contact
    const contactToRemove = profile.contacts.find(contact => contact.id === id);
    if (contactToRemove.isPrimary) {
      alert('You cannot remove the primary contact. Please set another contact as primary first.');
      return;
    }
    // Remove the contact
    setProfile({
      ...profile,
      contacts: profile.contacts.filter(contact => contact.id !== id)
    });
  };
  const formatSecurityTimer = () => {
    const minutes = Math.floor(securityTimer / 60);
    const seconds = securityTimer % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  // Request to view full bank details
  const requestViewBankDetails = accountId => {
    setPasswordVerification({
      showVerification: true,
      password: '',
      isVerifying: false,
      error: '',
      bankAccountToView: accountId
    });
  };
  // Verify password to view bank details
  const verifyPasswordForBankDetails = () => {
    if (!passwordVerification.password) {
      setPasswordVerification({
        ...passwordVerification,
        error: 'Please enter your password'
      });
      return;
    }
    setPasswordVerification({
      ...passwordVerification,
      isVerifying: true,
      error: ''
    });
    // Simulate password verification
    setTimeout(() => {
      // In a real app, this would check against the user's actual password
      const isCorrect = passwordVerification.password === 'password'; // Demo password
      if (isCorrect) {
        // Set this account as visible
        setVisibleBankAccounts({
          ...visibleBankAccounts,
          [passwordVerification.bankAccountToView]: true
        });
        // Reset verification state
        setPasswordVerification({
          showVerification: false,
          password: '',
          isVerifying: false,
          error: '',
          bankAccountToView: null
        });
        // Auto-hide after 2 minutes for security
        setTimeout(() => {
          setVisibleBankAccounts({
            ...visibleBankAccounts,
            [passwordVerification.bankAccountToView]: false
          });
        }, 120000); // 2 minutes
      } else {
        setPasswordVerification({
          ...passwordVerification,
          isVerifying: false,
          error: 'Incorrect password. Please try again.'
        });
      }
    }, 1000);
  };
  // Cancel password verification
  const cancelPasswordVerification = () => {
    setPasswordVerification({
      showVerification: false,
      password: '',
      isVerifying: false,
      error: '',
      bankAccountToView: null
    });
  };
  const renderBankAccount = (account, index) => {
    const isVisible = visibleBankAccounts[account.id];
    return <div key={account.id} className={`${index > 0 ? 'border-t border-gray-200' : ''}`} data-id="element-1226">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center bg-gray-50" data-id="element-1227">
          <div className="flex items-center" data-id="element-1228">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-1229">
              {account.nickname || `Account ${index + 1}`}
            </h4>
            {account.isPrimary && <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800" data-id="element-1230">
                Primary
              </span>}
          </div>
          <div className="flex space-x-2" data-id="element-1231">
            {!account.isPrimary && <button type="button" onClick={() => setPrimaryAccount(account.id)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1232">
                Set as Primary
              </button>}
            <button type="button" onClick={() => startBankDetailsUpdate(account)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1233">
              <LockIcon className="h-3 w-3 mr-1" data-id="element-1234" />
              Edit
            </button>
            {!account.isPrimary && <button type="button" onClick={() => removeAccount(account.id)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-1235">
                <TrashIcon className="h-3 w-3 mr-1" data-id="element-1236" />
                Remove
              </button>}
          </div>
        </div>
        <dl className="divide-y divide-gray-200" data-id="element-1237">
          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-1238">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1239">Bank Name</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-1240">
              {account.bankName || 'Not provided'}
            </dd>
          </div>
          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-1241">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1242">
              Account Number
            </dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center" data-id="element-1243">
              {isVisible ? <span data-id="element-1244">{account.accountNumber || 'Not provided'}</span> : <>
                  <span data-id="element-1245">
                    {account.accountNumber ? '••••' + account.accountNumber.slice(-4) : 'Not provided'}
                  </span>
                  {account.accountNumber && <button type="button" onClick={() => requestViewBankDetails(account.id)} className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none" data-id="element-1246">
                      <EyeIcon className="h-3 w-3 mr-1" data-id="element-1247" />
                      Show
                    </button>}
                </>}
            </dd>
          </div>
          <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-1248">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1249">
              Routing Number
            </dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex items-center" data-id="element-1250">
              {isVisible ? <span data-id="element-1251">{account.routingNumber || 'Not provided'}</span> : <>
                  <span data-id="element-1252">
                    {account.routingNumber ? '••••' + account.routingNumber.slice(-4) : 'Not provided'}
                  </span>
                  {account.routingNumber && <button type="button" onClick={() => requestViewBankDetails(account.id)} className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none" data-id="element-1253">
                      <EyeIcon className="h-3 w-3 mr-1" data-id="element-1254" />
                      Show
                    </button>}
                </>}
            </dd>
          </div>
        </dl>
      </div>;
  };
  return <div data-id="element-1255">
      <div className="mb-8" data-id="element-1256">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1257">Vendor Profile</h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-1258">
          View and manage your account information
        </p>
      </div>
      {(bankUpdateSuccess || updateSuccess) && <div className="mb-6 rounded-md bg-green-50 p-4" data-id="element-1259">
          <div className="flex" data-id="element-1260">
            <div className="flex-shrink-0" data-id="element-1261">
              <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1262" />
            </div>
            <div className="ml-3" data-id="element-1263">
              <p className="text-sm font-medium text-green-800" data-id="element-1264">
                {bankUpdateSuccess ? 'Banking details updated successfully' : 'Profile information updated successfully'}
              </p>
            </div>
          </div>
        </div>}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-1265">
        <div className="px-4 py-5 sm:px-6 flex justify-between" data-id="element-1266">
          <div data-id="element-1267">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-1268">
              Account Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-1269">
              Company details and preferences
            </p>
          </div>
          {!isEditing && <button type="button" onClick={() => setIsEditing(true)} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1270">
              Edit Profile
            </button>}
        </div>
        <div className="border-t border-gray-200" data-id="element-1271">
          {isEditing ? <form onSubmit={handleSubmit} className="px-4 py-5 sm:px-6" data-id="element-1272">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-1273">
                <div className="sm:col-span-3" data-id="element-1274">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-1275">
                    Company Name
                  </label>
                  <div className="mt-1" data-id="element-1276">
                    <input type="text" name="name" id="name" value={profile.name} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1277" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-1278">
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-1279">
                    Country <span className="text-red-500" data-id="element-1280">*</span>
                  </label>
                  <div className="mt-1" data-id="element-1281">
                    <select id="country" name="country" value={profile.country} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-1282">
                      <option value="" data-id="element-1283">Select country</option>
                      <option value="US" data-id="element-1284">United States</option>
                      <option value="CA" data-id="element-1285">Canada</option>
                      <option value="GB" data-id="element-1286">United Kingdom</option>
                      <option value="AU" data-id="element-1287">Australia</option>
                      <option value="DE" data-id="element-1288">Germany</option>
                      <option value="FR" data-id="element-1289">France</option>
                      <option value="JP" data-id="element-1290">Japan</option>
                      <option value="CN" data-id="element-1291">China</option>
                      <option value="IN" data-id="element-1292">India</option>
                      <option value="BR" data-id="element-1293">Brazil</option>
                      <option value="MX" data-id="element-1294">Mexico</option>
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-1295">
                  <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-1296">
                    Currency <span className="text-red-500" data-id="element-1297">*</span>
                  </label>
                  <div className="mt-1" data-id="element-1298">
                    <select id="currency" name="currency" value={profile.currency} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-1299">
                      <option value="" data-id="element-1300">Select currency</option>
                      <option value="USD" data-id="element-1301">USD - US Dollar</option>
                      <option value="EUR" data-id="element-1302">EUR - Euro</option>
                      <option value="GBP" data-id="element-1303">GBP - British Pound</option>
                      <option value="CAD" data-id="element-1304">CAD - Canadian Dollar</option>
                      <option value="AUD" data-id="element-1305">AUD - Australian Dollar</option>
                      <option value="JPY" data-id="element-1306">JPY - Japanese Yen</option>
                      <option value="CNY" data-id="element-1307">CNY - Chinese Yuan</option>
                      <option value="INR" data-id="element-1308">INR - Indian Rupee</option>
                      <option value="BRL" data-id="element-1309">BRL - Brazilian Real</option>
                      <option value="MXN" data-id="element-1310">MXN - Mexican Peso</option>
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-1311">
                  <label htmlFor="taxId" className="block text-sm font-medium text-gray-700" data-id="element-1312">
                    Tax ID / VAT Number
                  </label>
                  <div className="mt-1" data-id="element-1313">
                    <input type="text" name="taxId" id="taxId" value={profile.taxId} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1314" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-1315">
                  <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700" data-id="element-1316">
                    Payment Terms
                  </label>
                  <div className="mt-1" data-id="element-1317">
                    <select id="paymentTerms" name="paymentTerms" value={profile.paymentTerms} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1318">
                      <option value="" data-id="element-1319">Select payment terms</option>
                      <option value="net15" data-id="element-1320">Net 15</option>
                      <option value="net30" data-id="element-1321">Net 30</option>
                      <option value="net45" data-id="element-1322">Net 45</option>
                      <option value="net60" data-id="element-1323">Net 60</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-1324">
                <button type="button" onClick={() => setIsEditing(false)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1325">
                  Cancel
                </button>
                <button type="submit" disabled={isSaving} className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1326">
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form> : <dl className="divide-y divide-gray-200" data-id="element-1327">
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-1328">
                <dt className="text-sm font-medium text-gray-500 flex items-center" data-id="element-1329">
                  <BuildingIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-1330" />
                  Company Name
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-1331">
                  {profile.name || 'Not provided'}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-1332">
                <dt className="text-sm font-medium text-gray-500 flex items-center" data-id="element-1333">
                  <GlobeIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-1334" />
                  Country
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-1335">
                  {profile.country ? <div className="flex items-center" data-id="element-1336">
                      <span className={`mr-2 fi fi-${profile.country.toLowerCase()}`} data-id="element-1337"></span>
                      {profile.country === 'US' ? 'United States' : profile.country === 'CA' ? 'Canada' : profile.country === 'GB' ? 'United Kingdom' : profile.country === 'AU' ? 'Australia' : profile.country === 'DE' ? 'Germany' : profile.country === 'FR' ? 'France' : profile.country === 'JP' ? 'Japan' : profile.country === 'CN' ? 'China' : profile.country === 'IN' ? 'India' : profile.country === 'BR' ? 'Brazil' : profile.country === 'MX' ? 'Mexico' : profile.country}
                    </div> : <span className="text-yellow-600 font-medium" data-id="element-1338">
                      Required - Please update
                    </span>}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-1339">
                <dt className="text-sm font-medium text-gray-500 flex items-center" data-id="element-1340">
                  <DollarSignIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-1341" />
                  Currency
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-1342">
                  {profile.currency ? `${profile.currency} - ${profile.currency === 'USD' ? 'US Dollar' : profile.currency === 'EUR' ? 'Euro' : profile.currency === 'GBP' ? 'British Pound' : profile.currency === 'CAD' ? 'Canadian Dollar' : profile.currency === 'AUD' ? 'Australian Dollar' : profile.currency === 'JPY' ? 'Japanese Yen' : profile.currency === 'CNY' ? 'Chinese Yuan' : profile.currency === 'INR' ? 'Indian Rupee' : profile.currency === 'BRL' ? 'Brazilian Real' : profile.currency === 'MXN' ? 'Mexican Peso' : ''}` : <span className="text-yellow-600 font-medium" data-id="element-1343">
                      Required - Please update
                    </span>}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-1344">
                <dt className="text-sm font-medium text-gray-500" data-id="element-1345">
                  Tax ID / VAT Number
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-1346">
                  {profile.taxId || 'Not provided'}
                </dd>
              </div>
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6" data-id="element-1347">
                <dt className="text-sm font-medium text-gray-500" data-id="element-1348">
                  Payment Terms
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2" data-id="element-1349">
                  {profile.paymentTerms === 'net15' ? 'Net 15' : profile.paymentTerms === 'net30' ? 'Net 30' : profile.paymentTerms === 'net45' ? 'Net 45' : profile.paymentTerms === 'net60' ? 'Net 60' : 'Not specified'}
                </dd>
              </div>
            </dl>}
        </div>
      </div>
      {/* Addresses Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-1350">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-1351">
          <div data-id="element-1352">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-1353">
              <MapPinIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-1354" />
              Addresses
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-1355">
              Billing and shipping addresses
            </p>
          </div>
          {!isEditingAddress && !isEditing && <button type="button" onClick={startAddNewAddress} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1356">
              <PlusCircleIcon className="h-4 w-4 mr-1" data-id="element-1357" />
              Add Address
            </button>}
        </div>
        <div className="border-t border-gray-200" data-id="element-1358">
          {isEditingAddress ? <div className="px-4 py-5 sm:px-6" data-id="element-1359">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-1360">
                <div className="sm:col-span-3" data-id="element-1361">
                  <label htmlFor="addressType" className="block text-sm font-medium text-gray-700" data-id="element-1362">
                    Address Type
                  </label>
                  <div className="mt-1" data-id="element-1363">
                    <select id="addressType" name="type" value={tempAddressDetails.type} onChange={handleAddressDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1364">
                      <option value="Billing" data-id="element-1365">Billing</option>
                      <option value="Shipping" data-id="element-1366">Shipping</option>
                      <option value="Both" data-id="element-1367">Billing & Shipping</option>
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-1368">
                  <label htmlFor="isPrimaryAddress" className="block text-sm font-medium text-gray-700" data-id="element-1369">
                    Primary Address
                  </label>
                  <div className="mt-2" data-id="element-1370">
                    <div className="flex items-center" data-id="element-1371">
                      <input id="isPrimaryAddress" name="isPrimary" type="checkbox" checked={tempAddressDetails.isPrimary} onChange={e => setTempAddressDetails({
                    ...tempAddressDetails,
                    isPrimary: e.target.checked
                  })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" disabled={profile.addresses.length === 0} data-id="element-1372" />
                      <label htmlFor="isPrimaryAddress" className="ml-2 block text-sm text-gray-900" data-id="element-1373">
                        Set as primary address
                      </label>
                    </div>
                    {profile.addresses.length === 0 && <p className="mt-1 text-xs text-gray-500" data-id="element-1374">
                        First address is automatically set as primary
                      </p>}
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-1375">
                  <label htmlFor="street" className="block text-sm font-medium text-gray-700" data-id="element-1376">
                    Street Address
                  </label>
                  <div className="mt-1" data-id="element-1377">
                    <input type="text" name="street" id="street" value={tempAddressDetails.street} onChange={handleAddressDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1378" />
                  </div>
                </div>
                <div className="sm:col-span-2" data-id="element-1379">
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700" data-id="element-1380">
                    City
                  </label>
                  <div className="mt-1" data-id="element-1381">
                    <input type="text" name="city" id="city" value={tempAddressDetails.city} onChange={handleAddressDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1382" />
                  </div>
                </div>
                <div className="sm:col-span-2" data-id="element-1383">
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700" data-id="element-1384">
                    State / Province
                  </label>
                  <div className="mt-1" data-id="element-1385">
                    <input type="text" name="state" id="state" value={tempAddressDetails.state} onChange={handleAddressDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1386" />
                  </div>
                </div>
                <div className="sm:col-span-2" data-id="element-1387">
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700" data-id="element-1388">
                    Postal Code
                  </label>
                  <div className="mt-1" data-id="element-1389">
                    <input type="text" name="postalCode" id="postalCode" value={tempAddressDetails.postalCode} onChange={handleAddressDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1390" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-1391">
                  <label htmlFor="addressCountry" className="block text-sm font-medium text-gray-700" data-id="element-1392">
                    Country
                  </label>
                  <div className="mt-1" data-id="element-1393">
                    <select id="addressCountry" name="country" value={tempAddressDetails.country} onChange={handleAddressDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1394">
                      <option value="" data-id="element-1395">Select country</option>
                      <option value="US" data-id="element-1396">United States</option>
                      <option value="CA" data-id="element-1397">Canada</option>
                      <option value="GB" data-id="element-1398">United Kingdom</option>
                      <option value="AU" data-id="element-1399">Australia</option>
                      <option value="DE" data-id="element-1400">Germany</option>
                      <option value="FR" data-id="element-1401">France</option>
                      <option value="JP" data-id="element-1402">Japan</option>
                      <option value="CN" data-id="element-1403">China</option>
                      <option value="IN" data-id="element-1404">India</option>
                      <option value="BR" data-id="element-1405">Brazil</option>
                      <option value="MX" data-id="element-1406">Mexico</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-1407">
                <button type="button" onClick={cancelAddressUpdate} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1408">
                  Cancel
                </button>
                <button type="button" onClick={submitAddressUpdate} className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1409">
                  {editingAddressId ? 'Update Address' : 'Add Address'}
                </button>
              </div>
            </div> : <div data-id="element-1410">
              {profile.addresses.length === 0 ? <div className="px-4 py-5 sm:px-6 text-center" data-id="element-1411">
                  <p className="text-sm text-gray-500" data-id="element-1412">
                    No addresses added yet.
                  </p>
                  <button type="button" onClick={startAddNewAddress} className="mt-3 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1413">
                    <PlusCircleIcon className="h-4 w-4 mr-2" data-id="element-1414" />
                    Add Your First Address
                  </button>
                </div> : <div data-id="element-1415">
                  {profile.addresses.map((address, index) => <div key={address.id} className={`${index > 0 ? 'border-t border-gray-200' : ''}`} data-id="element-1416">
                      <div className="px-4 py-5 sm:px-6 flex justify-between items-center bg-gray-50" data-id="element-1417">
                        <div className="flex items-center" data-id="element-1418">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-1419">
                            {address.type} Address
                          </h4>
                          {address.isPrimary && <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800" data-id="element-1420">
                              Primary
                            </span>}
                        </div>
                        <div className="flex space-x-2" data-id="element-1421">
                          {!address.isPrimary && <button type="button" onClick={() => setPrimaryAddress(address.id)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1422">
                              Set as Primary
                            </button>}
                          <button type="button" onClick={() => startAddressUpdate(address)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1423">
                            Edit
                          </button>
                          {!address.isPrimary && <button type="button" onClick={() => removeAddress(address.id)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-1424">
                              <TrashIcon className="h-3 w-3 mr-1" data-id="element-1425" />
                              Remove
                            </button>}
                        </div>
                      </div>
                      <div className="px-4 py-5 sm:px-6" data-id="element-1426">
                        <p className="text-sm text-gray-700" data-id="element-1427">
                          {address.street && address.street}
                          {(address.city || address.state || address.postalCode) && <br data-id="element-1428" />}
                          {address.city && `${address.city}, `}
                          {address.state && `${address.state} `}
                          {address.postalCode && address.postalCode}
                          {address.country && <span className="block mt-1" data-id="element-1429">
                              {address.country === 'US' ? 'United States' : address.country === 'CA' ? 'Canada' : address.country === 'GB' ? 'United Kingdom' : address.country === 'AU' ? 'Australia' : address.country === 'DE' ? 'Germany' : address.country === 'FR' ? 'France' : address.country === 'JP' ? 'Japan' : address.country === 'CN' ? 'China' : address.country === 'IN' ? 'India' : address.country === 'BR' ? 'Brazil' : address.country === 'MX' ? 'Mexico' : address.country}
                            </span>}
                        </p>
                      </div>
                    </div>)}
                </div>}
            </div>}
        </div>
      </div>
      {/* Contacts Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-1430">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-1431">
          <div data-id="element-1432">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-1433">
              <UserIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-1434" />
              Contacts
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-1435">
              People to contact at your organization
            </p>
          </div>
          {!isEditingContact && !isEditing && <button type="button" onClick={startAddNewContact} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1436">
              <PlusCircleIcon className="h-4 w-4 mr-1" data-id="element-1437" />
              Add Contact
            </button>}
        </div>
        <div className="border-t border-gray-200" data-id="element-1438">
          {isEditingContact ? <div className="px-4 py-5 sm:px-6" data-id="element-1439">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-1440">
                <div className="sm:col-span-3" data-id="element-1441">
                  <label htmlFor="contactName" className="block text-sm font-medium text-gray-700" data-id="element-1442">
                    Full Name
                  </label>
                  <div className="mt-1" data-id="element-1443">
                    <input type="text" name="name" id="contactName" value={tempContactDetails.name} onChange={handleContactDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1444" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-1445">
                  <label htmlFor="contactTitle" className="block text-sm font-medium text-gray-700" data-id="element-1446">
                    Job Title
                  </label>
                  <div className="mt-1" data-id="element-1447">
                    <input type="text" name="title" id="contactTitle" value={tempContactDetails.title} onChange={handleContactDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1448" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-1449">
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700" data-id="element-1450">
                    Email Address
                  </label>
                  <div className="mt-1" data-id="element-1451">
                    <input type="email" name="email" id="contactEmail" value={tempContactDetails.email} onChange={handleContactDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1452" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-1453">
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700" data-id="element-1454">
                    Phone Number
                  </label>
                  <div className="mt-1" data-id="element-1455">
                    <input type="text" name="phone" id="contactPhone" value={tempContactDetails.phone} onChange={handleContactDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1456" />
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-1457">
                  <div className="flex items-start" data-id="element-1458">
                    <div className="flex items-center h-5" data-id="element-1459">
                      <input id="isPrimaryContact" name="isPrimary" type="checkbox" checked={tempContactDetails.isPrimary} onChange={e => setTempContactDetails({
                    ...tempContactDetails,
                    isPrimary: e.target.checked
                  })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" disabled={profile.contacts.length === 0} data-id="element-1460" />
                    </div>
                    <div className="ml-3 text-sm" data-id="element-1461">
                      <label htmlFor="isPrimaryContact" className="font-medium text-gray-700" data-id="element-1462">
                        Primary Contact
                      </label>
                      <p className="text-gray-500" data-id="element-1463">
                        This person will be the main point of contact for your
                        account.
                      </p>
                      {profile.contacts.length === 0 && <p className="mt-1 text-xs text-gray-500" data-id="element-1464">
                          First contact is automatically set as primary
                        </p>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-1465">
                <button type="button" onClick={cancelContactUpdate} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1466">
                  Cancel
                </button>
                <button type="button" onClick={submitContactUpdate} className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1467">
                  {editingContactId ? 'Update Contact' : 'Add Contact'}
                </button>
              </div>
            </div> : <div data-id="element-1468">
              {profile.contacts.length === 0 ? <div className="px-4 py-5 sm:px-6 text-center" data-id="element-1469">
                  <p className="text-sm text-gray-500" data-id="element-1470">
                    No contacts added yet.
                  </p>
                  <button type="button" onClick={startAddNewContact} className="mt-3 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1471">
                    <PlusCircleIcon className="h-4 w-4 mr-2" data-id="element-1472" />
                    Add Your First Contact
                  </button>
                </div> : <div data-id="element-1473">
                  {profile.contacts.map((contact, index) => <div key={contact.id} className={`${index > 0 ? 'border-t border-gray-200' : ''}`} data-id="element-1474">
                      <div className="px-4 py-5 sm:px-6 flex justify-between items-center bg-gray-50" data-id="element-1475">
                        <div className="flex items-center" data-id="element-1476">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-1477">
                            {contact.name}
                            {contact.title && `, ${contact.title}`}
                          </h4>
                          {contact.isPrimary && <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800" data-id="element-1478">
                              Primary
                            </span>}
                        </div>
                        <div className="flex space-x-2" data-id="element-1479">
                          {!contact.isPrimary && <button type="button" onClick={() => setPrimaryContact(contact.id)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1480">
                              Set as Primary
                            </button>}
                          <button type="button" onClick={() => startContactUpdate(contact)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1481">
                            Edit
                          </button>
                          {!contact.isPrimary && <button type="button" onClick={() => removeContact(contact.id)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-1482">
                              <TrashIcon className="h-3 w-3 mr-1" data-id="element-1483" />
                              Remove
                            </button>}
                        </div>
                      </div>
                      <div className="px-4 py-5 sm:px-6" data-id="element-1484">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1485">
                          <div className="flex items-center" data-id="element-1486">
                            <MailIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-1487" />
                            <span className="text-sm text-gray-700" data-id="element-1488">
                              {contact.email || 'No email provided'}
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-1489">
                            <PhoneIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-1490" />
                            <span className="text-sm text-gray-700" data-id="element-1491">
                              {contact.phone || 'No phone provided'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>}
            </div>}
        </div>
      </div>
      {/* Password verification modal */}
      {passwordVerification.showVerification && <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center" data-id="element-1492">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" data-id="element-1493"></div>
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full m-4 p-6" data-id="element-1494">
            <div className="mb-4" data-id="element-1495">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1496">
                Security Verification
              </h3>
              <p className="text-sm text-gray-500" data-id="element-1497">
                Please enter your password to view sensitive banking information
              </p>
            </div>
            {passwordVerification.error && <div className="mb-4 rounded-md bg-red-50 p-4" data-id="element-1498">
                <div className="flex" data-id="element-1499">
                  <div className="flex-shrink-0" data-id="element-1500">
                    <AlertCircleIcon className="h-5 w-5 text-red-400" data-id="element-1501" />
                  </div>
                  <div className="ml-3" data-id="element-1502">
                    <p className="text-sm font-medium text-red-800" data-id="element-1503">
                      {passwordVerification.error}
                    </p>
                  </div>
                </div>
              </div>}
            <div className="mb-4" data-id="element-1504">
              <label htmlFor="security-password" className="block text-sm font-medium text-gray-700" data-id="element-1505">
                Password
              </label>
              <input type="password" id="security-password" value={passwordVerification.password} onChange={e => setPasswordVerification({
            ...passwordVerification,
            password: e.target.value
          })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Enter your password" data-id="element-1506" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1507">
                For demo purposes, use "password"
              </p>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense" data-id="element-1508">
              <button type="button" onClick={verifyPasswordForBankDetails} disabled={passwordVerification.isVerifying} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50" data-id="element-1509">
                {passwordVerification.isVerifying ? <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1510">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1511"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1512"></path>
                    </svg>
                    Verifying...
                  </> : 'Verify'}
              </button>
              <button type="button" onClick={cancelPasswordVerification} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm" data-id="element-1513">
                Cancel
              </button>
            </div>
          </div>
        </div>}
      {/* Banking Information Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1514">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-1515">
          <div data-id="element-1516">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-1517">
              <ShieldIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-1518" />
              Banking Information
              <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800" data-id="element-1519">
                Secure
              </span>
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-1520">
              Payment details for receiving funds
            </p>
          </div>
          {!isEditingBank && !isEditing && <button type="button" onClick={startAddNewBank} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1521">
              <PlusCircleIcon className="h-4 w-4 mr-1" data-id="element-1522" />
              Add Bank Account
            </button>}
        </div>
        <div className="border-t border-gray-200" data-id="element-1523">
          {isEditingBank ? <div className="px-4 py-5 sm:px-6" data-id="element-1524">
              {!showBankVerification ? <div data-id="element-1525">
                  <div className="mb-4 rounded-md bg-yellow-50 p-4" data-id="element-1526">
                    <div className="flex" data-id="element-1527">
                      <div className="flex-shrink-0" data-id="element-1528">
                        <AlertCircleIcon className="h-5 w-5 text-yellow-400" data-id="element-1529" />
                      </div>
                      <div className="ml-3" data-id="element-1530">
                        <h3 className="text-sm font-medium text-yellow-800" data-id="element-1531">
                          Security Notice
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700" data-id="element-1532">
                          <p data-id="element-1533">
                            {isAddingBank ? 'Adding' : 'Updating'} your banking
                            information requires additional verification. We
                            will send verification codes to your email and phone
                            number.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-1534">
                      <div className="sm:col-span-3" data-id="element-1535">
                        <label htmlFor="nickname" className="block text-sm font-medium text-gray-700" data-id="element-1536">
                          Account Nickname
                        </label>
                        <div className="mt-1" data-id="element-1537">
                          <input type="text" name="nickname" id="nickname" value={tempBankDetails.nickname} onChange={handleBankDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="e.g. Main Account, EUR Account" data-id="element-1538" />
                        </div>
                      </div>
                      <div className="sm:col-span-3" data-id="element-1539">
                        <label htmlFor="isPrimary" className="block text-sm font-medium text-gray-700" data-id="element-1540">
                          Account Type
                        </label>
                        <div className="mt-2" data-id="element-1541">
                          <div className="flex items-center" data-id="element-1542">
                            <input id="isPrimary" name="isPrimary" type="checkbox" checked={tempBankDetails.isPrimary} onChange={e => setTempBankDetails({
                        ...tempBankDetails,
                        isPrimary: e.target.checked
                      })} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" disabled={profile.bankAccounts.length === 0} data-id="element-1543" />
                            <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-900" data-id="element-1544">
                              Set as primary account
                            </label>
                          </div>
                          {profile.bankAccounts.length === 0 && <p className="mt-1 text-xs text-gray-500" data-id="element-1545">
                              First account is automatically set as primary
                            </p>}
                        </div>
                      </div>
                      <div className="sm:col-span-3" data-id="element-1546">
                        <label htmlFor="bankName" className="block text-sm font-medium text-gray-700" data-id="element-1547">
                          Bank Name
                        </label>
                        <div className="mt-1" data-id="element-1548">
                          <input type="text" name="bankName" id="bankName" value={tempBankDetails.bankName} onChange={handleBankDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1549" />
                        </div>
                      </div>
                      <div className="sm:col-span-3" data-id="element-1550">
                        <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700" data-id="element-1551">
                          Account Number
                        </label>
                        <div className="mt-1" data-id="element-1552">
                          <input type="text" name="accountNumber" id="accountNumber" value={tempBankDetails.accountNumber} onChange={handleBankDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1553" />
                        </div>
                      </div>
                      <div className="sm:col-span-3" data-id="element-1554">
                        <label htmlFor="routingNumber" className="block text-sm font-medium text-gray-700" data-id="element-1555">
                          Routing Number
                        </label>
                        <div className="mt-1" data-id="element-1556">
                          <input type="text" name="routingNumber" id="routingNumber" value={tempBankDetails.routingNumber} onChange={handleBankDetailsChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-1557" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end space-x-3" data-id="element-1558">
                      <button type="button" onClick={cancelBankUpdate} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1559">
                        Cancel
                      </button>
                      <button type="button" onClick={requestVerificationCodes} className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50" data-id="element-1560">
                        <KeyIcon className="h-4 w-4 mr-2" data-id="element-1561" />
                        Continue with Verification
                      </button>
                    </div>
                  </div>
                </div> : <div data-id="element-1562">
                  <div className="mb-4 rounded-md bg-blue-50 p-4" data-id="element-1563">
                    <div className="flex" data-id="element-1564">
                      <div className="flex-shrink-0" data-id="element-1565">
                        <ShieldIcon className="h-5 w-5 text-blue-400" data-id="element-1566" />
                      </div>
                      <div className="ml-3" data-id="element-1567">
                        <h3 className="text-sm font-medium text-blue-800" data-id="element-1568">
                          Verification Required
                        </h3>
                        <div className="mt-2 text-sm text-blue-700" data-id="element-1569">
                          <p data-id="element-1570">
                            Please enter the verification codes sent to your
                            email ({profile.email}) and phone ({profile.phone}).
                          </p>
                          <p className="mt-1" data-id="element-1571">
                            Time remaining:{' '}
                            <span className="font-medium" data-id="element-1572">
                              {formatSecurityTimer()}
                            </span>
                          </p>
                          <p className="mt-2 text-xs" data-id="element-1573">
                            For demo purposes: Use "123456" for email code and
                            "654321" for SMS code
                          </p>
                        </div>
                      </div>
                    </div>
                    {verificationError && <div className="mb-4 rounded-md bg-red-50 p-4" data-id="element-1574">
                        <div className="flex" data-id="element-1575">
                          <div className="flex-shrink-0" data-id="element-1576">
                            <AlertCircleIcon className="h-5 w-5 text-red-400" data-id="element-1577" />
                          </div>
                          <div className="ml-3" data-id="element-1578">
                            <p className="text-sm font-medium text-red-800" data-id="element-1579">
                              {verificationError}
                            </p>
                          </div>
                        </div>
                      </div>}
                    <div className="space-y-6" data-id="element-1580">
                      <div data-id="element-1581">
                        <label htmlFor="emailCode" className="block text-sm font-medium text-gray-700" data-id="element-1582">
                          Email Verification Code
                        </label>
                        <div className="mt-1 flex items-center" data-id="element-1583">
                          <input type="text" name="emailCode" id="emailCode" value={emailCode} onChange={e => setEmailCode(e.target.value)} maxLength={6} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Enter 6-digit code" disabled={emailVerified} data-id="element-1584" />
                          {emailVerified ? <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" data-id="element-1585" /> : <button type="button" onClick={verifyEmailCode} className="ml-2 inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1586">
                              Verify
                            </button>}
                        </div>
                      </div>
                      <div data-id="element-1587">
                        <label htmlFor="smsCode" className="block text-sm font-medium text-gray-700" data-id="element-1588">
                          SMS Verification Code
                        </label>
                        <div className="mt-1 flex items-center" data-id="element-1589">
                          <input type="text" name="smsCode" id="smsCode" value={smsCode} onChange={e => setSmsCode(e.target.value)} maxLength={6} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Enter 6-digit code" disabled={smsVerified} data-id="element-1590" />
                          {smsVerified ? <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" data-id="element-1591" /> : <button type="button" onClick={verifySmsCode} className="ml-2 inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1592">
                              Verify
                            </button>}
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end space-x-3" data-id="element-1593">
                      <button type="button" onClick={cancelBankUpdate} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1594">
                        Cancel
                      </button>
                      <button type="button" onClick={submitBankUpdate} disabled={!emailVerified || !smsVerified} className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50" data-id="element-1595">
                        <ShieldIcon className="h-4 w-4 mr-2" data-id="element-1596" />
                        {isAddingBank ? 'Add Bank Account' : 'Update Bank Account'}
                      </button>
                    </div>
                  </div>
                </div>}
            </div> : <div data-id="element-1597">
              {profile.bankAccounts.length === 0 ? <div className="px-4 py-5 sm:px-6 text-center" data-id="element-1598">
                  <p className="text-sm text-gray-500" data-id="element-1599">
                    No bank accounts added yet.
                  </p>
                  <button type="button" onClick={startAddNewBank} className="mt-3 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1600">
                    <PlusCircleIcon className="h-4 w-4 mr-2" data-id="element-1601" />
                    Add Your First Bank Account
                  </button>
                </div> : <div data-id="element-1602">
                  {profile.bankAccounts.map((account, index) => renderBankAccount(account, index))}
                </div>}
            </div>}
        </div>
      </div>
    </div>;
}